"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const react_plotly_js_1 = __importDefault(require("react-plotly.js"));
const maclaurin_1 = require("./maclaurin");
const CalculusVisualization = () => {
    var _a, _b, _c, _d;
    const [funcKey, setFuncKey] = (0, react_1.useState)('exp');
    const [terms, setTerms] = (0, react_1.useState)(12);
    // Track the current view range; when null, use function domain
    const [viewXMin, setViewXMin] = (0, react_1.useState)(null);
    const [viewXMax, setViewXMax] = (0, react_1.useState)(null);
    const [viewYMin, setViewYMin] = (0, react_1.useState)(null);
    const [viewYMax, setViewYMax] = (0, react_1.useState)(null);
    const series = (0, react_1.useMemo)(() => maclaurin_1.SERIES.find((s) => s.key === funcKey) || maclaurin_1.SERIES[0], [funcKey]);
    // Reset view when switching function
    react_1.default.useEffect(() => {
        setViewXMin(null);
        setViewXMax(null);
        setViewYMin(null);
        setViewYMax(null);
    }, [series]);
    // Determine sampling window
    const xMin = (_b = viewXMin !== null && viewXMin !== void 0 ? viewXMin : (_a = series.domain) === null || _a === void 0 ? void 0 : _a.min) !== null && _b !== void 0 ? _b : -2;
    const xMax = (_d = viewXMax !== null && viewXMax !== void 0 ? viewXMax : (_c = series.domain) === null || _c === void 0 ? void 0 : _c.max) !== null && _d !== void 0 ? _d : 2;
    // Auto sample based on current range; more samples when wider range
    const sampleCount = (0, react_1.useMemo)(() => {
        const span = Math.max(1, Math.abs(xMax - xMin));
        return Math.min(2000, Math.max(300, Math.floor(span * 150)));
    }, [xMin, xMax]);
    const xs = (0, react_1.useMemo)(() => (0, maclaurin_1.sampleRange)(xMin, xMax, sampleCount), [xMin, xMax, sampleCount]);
    // Actual function values
    const actualYs = (0, react_1.useMemo)(() => xs.map((x) => series.f(x)), [xs, series]);
    // Stable y-range derived ONLY from actual function unless user zooms Y
    const autoYRange = (0, react_1.useMemo)(() => {
        let minY = Infinity;
        let maxY = -Infinity;
        for (const y of actualYs) {
            if (Number.isFinite(y)) {
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }
        if (!Number.isFinite(minY) || !Number.isFinite(maxY) || minY === maxY) {
            minY = -2;
            maxY = 2;
        }
        const pad = (maxY - minY) * 0.12 + 0.25;
        return { min: minY - pad, max: maxY + pad };
    }, [actualYs]);
    const yMin = viewYMin !== null && viewYMin !== void 0 ? viewYMin : autoYRange.min;
    const yMax = viewYMax !== null && viewYMax !== void 0 ? viewYMax : autoYRange.max;
    // Series values; drop non-finite to avoid axis blow-ups
    const seriesYs = (0, react_1.useMemo)(() => xs.map((x) => {
        const y = (0, maclaurin_1.evaluateSeriesAt)(series, x, terms);
        return Number.isFinite(y) ? y : null;
    }), [xs, series, terms]);
    const handleRelayout = (e) => {
        if (e && typeof e === 'object') {
            // X range
            if (e['xaxis.autorange']) {
                setViewXMin(null);
                setViewXMax(null);
            }
            const xmin = e['xaxis.range[0]'];
            const xmax = e['xaxis.range[1]'];
            if (typeof xmin === 'number' && typeof xmax === 'number' && isFinite(xmin) && isFinite(xmax)) {
                setViewXMin(xmin);
                setViewXMax(xmax);
            }
            // Y range
            if (e['yaxis.autorange']) {
                setViewYMin(null);
                setViewYMax(null);
            }
            const ymin = e['yaxis.range[0]'];
            const ymax = e['yaxis.range[1]'];
            if (typeof ymin === 'number' && typeof ymax === 'number' && isFinite(ymin) && isFinite(ymax)) {
                setViewYMin(ymin);
                setViewYMax(ymax);
            }
        }
    };
    return (react_1.default.createElement("div", { className: "visualization" },
        react_1.default.createElement("h2", { className: "section-heading", style: { marginTop: 0 } }, "Maclaurin Series"),
        react_1.default.createElement("p", { style: { color: 'var(--muted)', marginBottom: 16 } }, "Compare a function to its Maclaurin series \u03A3 a\u2099 x\u207F around x = 0. Increase terms to see the approximation improve near the origin."),
        react_1.default.createElement("section", { className: "panel params", "aria-label": "Graph parameters" },
            react_1.default.createElement("div", { className: "params-grid" },
                react_1.default.createElement("label", { className: "field" },
                    react_1.default.createElement("span", { className: "label" }, "Function"),
                    react_1.default.createElement("select", { value: funcKey, onChange: (e) => setFuncKey(e.target.value) }, maclaurin_1.SERIES.map((s) => (react_1.default.createElement("option", { key: s.key, value: s.key }, s.label)))),
                    series.note && react_1.default.createElement("small", { className: "hint" }, series.note)),
                react_1.default.createElement("label", { className: "field" },
                    react_1.default.createElement("span", { className: "label" }, "Terms (n)"),
                    react_1.default.createElement("input", { type: "range", min: 1, max: 50, step: 1, value: terms, onChange: (e) => setTerms(parseInt(e.target.value, 10)) }),
                    react_1.default.createElement("div", { className: "value" }, terms)))),
        react_1.default.createElement(react_plotly_js_1.default, { data: [
                { x: xs, y: actualYs, type: 'scatter', mode: 'lines', line: { color: '#22d3ee', width: 2 }, name: `Actual: ${series.label}`, hovertemplate: 'x=%{x:.3f}<br>f(x)=%{y:.5f}<extra></extra>' },
                { x: xs, y: seriesYs, type: 'scatter', mode: 'lines', line: { color: '#a78bfa', width: 2 }, name: `Series (n=${terms})`, hovertemplate: 'x=%{x:.3f}<br>S(x)=%{y:.5f}<extra></extra>' }
            ], layout: {
                paper_bgcolor: 'rgba(0,0,0,0)',
                plot_bgcolor: 'rgba(0,0,0,0)',
                font: { color: '#e5e7eb' },
                margin: { l: 48, r: 16, t: 16, b: 36 },
                xaxis: { gridcolor: 'rgba(148,163,184,0.15)', range: [xMin, xMax] },
                yaxis: { gridcolor: 'rgba(148,163,184,0.15)', range: [yMin, yMax] },
                legend: { bgcolor: 'rgba(2,6,23,0.6)', bordercolor: 'var(--border)', borderwidth: 1, x: 1, xanchor: 'right', y: 1 }
            }, config: { responsive: true, displaylogo: false, modeBarButtonsToRemove: ['toImage'], scrollZoom: true }, style: { width: '100%', height: 460 }, onRelayout: handleRelayout }),
        react_1.default.createElement("section", { className: "panel", style: { marginTop: 16 } },
            react_1.default.createElement("h3", { className: "section-heading", style: { marginTop: 0 } }, "What is a Maclaurin Series?"),
            react_1.default.createElement("p", { style: { color: 'var(--muted)' } }, "A Maclaurin series is a Taylor series centered at x = 0. It expresses a function as an infinite sum of polynomial terms formed from derivatives at 0:"),
            react_1.default.createElement("pre", { style: { overflowX: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', padding: 12, borderRadius: 8 } }, `f(x) = \n  f(0) + f'(0) x + f''(0) x^2 / 2! + f'''(0) x^3 / 3! + ...`),
            react_1.default.createElement("ul", { style: { color: 'var(--muted)' } },
                react_1.default.createElement("li", null, "Near x = 0, adding more terms generally improves the approximation."),
                react_1.default.createElement("li", null, "Convergence depends on the function; some series work for all x (e.g., e^x), others only for |x| < 1."),
                react_1.default.createElement("li", null, "Even/odd symmetry simplifies which powers appear (e.g., cos uses even powers only).")))));
};
exports.default = CalculusVisualization;
